// src/components/Header.jsx
import React, { useState, useEffect, useCallback } from 'react';
// Importar Link como RouterLink y useLocation de react-router-dom
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown, Button } from 'react-bootstrap';


const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isHeroVisible, setIsHeroVisible] = useState(true);
    const location = useLocation(); // Hook para obtener la ruta actual

    // Determinar si estamos en la página principal (landing page)
    const isLandingPage = location.pathname === '/';

    const handleScroll = useCallback(() => {
        const offset = window.scrollY;
        // Solo buscar heroSection si estamos en la landing page
        const heroSection = isLandingPage ? document.querySelector('.hero-section-video') : null;
        let heroHeight = 400;
        if (heroSection) {
            heroHeight = heroSection.offsetHeight;
        }
        const headerElement = document.querySelector('.app-header');
        const headerHeight = headerElement ? headerElement.offsetHeight : 70;

        setScrolled(offset > 50);
        // La transparencia del header solo aplica si estamos en la landing page y el hero está visible
        setIsHeroVisible(isLandingPage && (offset < heroHeight - headerHeight));
    }, [isLandingPage]); // Añadir isLandingPage a las dependencias de useCallback

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Llamada inicial
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const navbarBaseClass = "app-header fixed-top";
    // La clase 'header-transparent' solo se aplica si isHeroVisible (que ahora depende de isLandingPage)
    const navbarDynamicClass = isHeroVisible ? "header-transparent" : "header-scrolled";
    const combinedNavbarClass = `${navbarBaseClass} ${navbarDynamicClass}`;

    const brandHighlightClass = isHeroVisible ? "brand-highlight-transparent" : "brand-highlight-scrolled";

    const loginButtonVariant = isHeroVisible ? "outline-light" : "outline-primary";
    const signupButtonVariant = isHeroVisible ? "light" : "success";

    const handleNavLinkClick = (e, targetId) => {
        const navbarToggler = document.querySelector('.navbar-toggler');
        const navbarCollapse = document.querySelector('.navbar-collapse');
        if (navbarCollapse && navbarCollapse.classList.contains('show')) {
            navbarToggler.click();
        }

        if (isLandingPage && targetId && targetId.startsWith("#")) {
            e.preventDefault();
            const element = document.getElementById(targetId.substring(1));
            if (element) {
                const headerOffset = document.querySelector('.app-header')?.offsetHeight || 70;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - headerOffset - 20;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
            }
        }
        // Si no es un enlace de anclaje en la landing page, dejará que RouterLink haga su trabajo
    };

    return (
        <Navbar expand="lg" className={combinedNavbarClass}>
            <Container>
                <Navbar.Brand as={RouterLink} to="/" className="site-brand">
                    <img
                        src="/logo-light.png"
                        alt="MyFit Menu AI Logo"
                        height="80"
                        className={`logo-img ${isHeroVisible ? "" : "logo-background"}`}
                    />
                </Navbar.Brand>

                <Navbar.Toggle aria-controls="main-navbar" />

                <Navbar.Collapse id="main-navbar">
                    <Nav className="me-auto">
                        {/* Enlaces condicionales para la Landing Page */}
                        {isLandingPage && (
                            <>
                                <Nav.Link
                                    className="nav-link-header"
                                    href="#features-section"
                                    onClick={(e) => handleNavLinkClick(e, "#features-section")}
                                >
                                    Features
                                </Nav.Link>
                                <Nav.Link
                                    className="nav-link-header"
                                    href="#how-it-works-section"
                                    onClick={(e) => handleNavLinkClick(e, "#how-it-works-section")}
                                >
                                    How It Works
                                </Nav.Link>
                                <Nav.Link
                                    className="nav-link-header"
                                    href="#testimonials-section"
                                    onClick={(e) => handleNavLinkClick(e, "#testimonials-section")}
                                >
                                    Testimonials
                                </Nav.Link>
                                <Nav.Link
                                    className="nav-link-header"
                                    href="#faq-section"
                                    onClick={(e) => handleNavLinkClick(e, "#faq-section")}
                                >
                                    FAQ
                                </Nav.Link>
                            </>
                        )}

                        {/* Enlaces a otras páginas (siempre visibles o según otra lógica) */}
                        <Nav.Link className="nav-link-header" as={RouterLink} to="/contact">Contact</Nav.Link>
                        <Nav.Link className="nav-link-header" as={RouterLink} to="/pricing">Pricing</Nav.Link>
                        <Nav.Link className="nav-link-header" as={RouterLink} to="/about">About</Nav.Link>
                    </Nav>

                    <Nav className="ms-auto d-flex align-items-center gap-2 gap-lg-3">
                        <NavDropdown
                            title={
                                <span className="language-dropdown-title">
                                    <img src="https://flagcdn.com/w20/gb.png" width="20" className="me-1" alt="EN" />
                                    Language
                                </span>
                            }
                            id="lang-dropdown"
                            menuVariant={isHeroVisible ? "dark" : "light"}
                            className="nav-link-header"
                        >
                            <NavDropdown.Item><img src="https://flagcdn.com/w20/gb.png" width="20" className="me-2" alt="English" />English</NavDropdown.Item>
                            <NavDropdown.Item><img src="https://flagcdn.com/w20/es.png" width="20" className="me-2" alt="Español" />Español</NavDropdown.Item>
                            <NavDropdown.Item><img src="https://flagcdn.com/w20/fr.png" width="20" className="me-2" alt="Français" />Français</NavDropdown.Item>
                        </NavDropdown>

                        <Button
                            as={RouterLink} to="/login"
                            variant={loginButtonVariant}
                            className="fw-medium px-3 py-2 rounded-pill login-button"
                            size="sm"
                        >
                            Login
                        </Button>

                        <Button
                            as={RouterLink} to="/register"
                            variant={signupButtonVariant}
                            className="fw-bold px-4 py-2 rounded-pill header-cta-button signup-button"
                        >
                            Get started
                        </Button>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};

export default Header;